import type * as React from "react";
import { useState } from "react";
import { AccessLevel, MediaType, type SchaefflerMediaDTO, TeaserType } from "~/features/cms/types/dto";
import { type CMSTeaserProps } from "~/features/cms/types/model";
import { ArrowLink, Link, RouterLink } from "~/common/components/Link";
import { isSiteRelativeUrl } from "~/utils/url/urlResolver";
import { Button, Card, CardActionArea, IconButton, styled, useMediaQuery } from "@schaeffler-commerce/design-system";
import { IconSize } from "@schaeffler-commerce/design-system";
import { TeaserToggleIcon } from "~/common/components/icon/TeaserToggleIcon";
import classNames from "classnames";
import { _BLANK, BLANK, NARROW } from "~/utils/constants";
import { MediasLogo, type MediasVariant } from "~/features/navigation/components/TopBar/MediasLogo";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { Menu, useSimpleMenuState } from "~/common/components/Menu/Menu";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import ConsentPopup from "~/features/cms/components/cmsTeaser/ConsentPopup";
import { isLoginTimeoutErrorVisible } from "~/features/auth/login/loginUtils";
import ToastMessage from "~/common/components/Messages/ToastMessage/ToastMessage";

import {
  checkIsLocked,
  checkIsVisible,
  userGaveMarketingConsent,
} from "~/features/cms/components/cmsTeaser/CMSTeaserIsLocked";
import { consentIntro } from "~/features/cms/components/cmsTeaser/CMSTeaserConsent";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { invalidateSession } from "~/app/api/pageData";
import { CMSMedia } from "~/features/cms/components/cmsMedia/CMSMedia";
import { trackButtonClick, trackRegister } from "~/app/analytics/ga/contentStream";
import { cleanupText, createMarkup } from "~/features/cms/utils/markup";
import { isMarketingConsentEnabled } from "~/utils/features";
import { Carousel } from "~/common/components/carousel/Carousel";
import { MEDIA_QUERY_MIN_TABLET } from "@schaeffler-commerce/design-system";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { getButtonSize, getButtonType } from "~/features/cms/utils/helpers";
import { resolveUrl } from "~/utils/url/urlResolver";
import { FontSize, getFontStyle } from "@schaeffler-commerce/design-system";
import { C_ON_SURFACE_VARIANT, C_OUTLINE_VARIANT, C_PRIMARY } from "@schaeffler-commerce/design-system";
import { makeExternallyStyledElement } from "~/features/cms/utils/styles";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import { useDispatch } from "react-redux";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";
import type { JSX } from "react";

const CMSTeaser = ({
  buttonSize = "DEFAULT",
  teaserType = TeaserType.STANDARD,
  headline,
  subHeadline,
  linkLabel,
  contentDropdown,
  toggles = false,
  buttonLabel,
  medias,
  imageClickable = false, // Off by default in favor of the main CTAs
  content,
  linkUrl,
  buttonType,
  index = 0,
  target = "",
  gated, // Used in Publication
  /**
   * "requires" marketing consent, not "gave" marketing consent
   */
  marketingConsent, // For Tests
  accessLevel = AccessLevel.general, // Used in Publication
  showMediaMetaData = false, // Used in Publication
  mediaMetaData, // Used in Publication
  onToggle,
  setContent,
  isExpanded = false,
  wideness = NARROW,
  className,
  mirrored = false,

  rootStyles,
  headlineStyles,
  subHeadlineStyles,
}: CMSTeaserProps) => {
  // These are "required" properties to make the teaser make sense.
  // Probably needs more valid combinations.
  const showContentBox: boolean = !!(headline || subHeadline || content || linkLabel || buttonLabel);

  const { t } = useTranslation();

  content = cleanupText(content);
  headline = cleanupText(headline);
  subHeadline = cleanupText(subHeadline);

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  // Sanitizing the capitalized variables passed from JSP
  let accessLevelStr = accessLevel?.toLowerCase();

  const buttonProps = {
    ...getButtonSize(buttonSize),
    ...getButtonType(buttonType),
  };

  if (accessLevelStr === "plusbusiness") {
    accessLevelStr = "plus-business";
  }

  const useInvisibleLogic = teaserType === TeaserType.CTA;

  const isLocked = checkIsLocked({
    gated,
    marketingConsent,
    accessLevel,
  });

  let isVisible = true;

  if (useInvisibleLogic) {
    isVisible = checkIsVisible();
  }

  const {
    val: marketingConsentPopupOpen,
    setTrue: openMarketingConsent,
    setFalse: closeMarketingConsent,
  } = useBooleanState(false);

  const dispatch = useDispatch();

  // For now the consent popup title will use the headlines or the buttonLabel if provided.
  const popupTitle = headline || subHeadline || buttonLabel;

  const onAcceptMarketingConsent = () => {
    invalidateSession();
    closeMarketingConsent();
  };

  const onSignInClick = () => {
    dispatch(openAuthOverlay("login", { alternativeGAAction: "Sign In Content Stream" }));
  };

  const onRegisterClick = () => {
    trackRegister();
    dispatch(openAuthOverlay("register"));
  };

  // Makes the whole teaser toggle the contentDropdown
  if (teaserType === TeaserType.DROPDOWN) {
    toggles = true;
  }

  if (teaserType === TeaserType.PUBLICATION) {
    buttonProps.color = "primary";
    buttonProps.variant = "contained";
    buttonProps.size = "medium";
  }

  const hasLink: boolean = !!linkUrl && Array.isArray(linkUrl) && !!linkUrl[0];

  let _linkUrl: string | undefined;

  if (linkUrl && linkUrl.length) {
    if (typeof linkUrl === "string") {
      _linkUrl = linkUrl;
    } else if (Array.isArray(linkUrl) && linkUrl[0].url) {
      _linkUrl = linkUrl[0].url;
    }
  }

  // Sets the default link to the first found url
  const [selectedLinkURL, setSelectedUrl] = useState<string | undefined>(_linkUrl);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>();
  const [isOpen, handleClick, handleClose, anchorEl] = useSimpleMenuState();

  /**
   * Should pass on the event to the parent CMSTeasers component.
   * Passes on the HTML in `contentDropdown` to the CMSTeasers component.
   **/
  const toggle = (e: React.MouseEvent<unknown>) => {
    e.stopPropagation();
    // Only when this is a Dropdown Teaser with content
    if (!!contentDropdown && typeof index !== "undefined" && !!onToggle && !!setContent) {
      onToggle(index);
      setContent(contentDropdown);
    }
  };

  const isLoggedIn = userIsLoggedIn();

  const isUrlVisible = teaserType !== TeaserType.CTA || !gated || (gated && !isLocked);

  /**
   * Main Call To Action click
   * @returns void
   */
  const click = (e: React.MouseEvent<unknown>) => {
    e.stopPropagation();

    const validConfig = selectedLinkURL || (gated && isLocked);

    if (!validConfig) {
      if (import.meta.env.DEV) {
        console.warn("Please add a valid linkUrl");
      }
      return;
    }

    if (teaserType !== TeaserType.CTA) {
      if (gated && isLocked) {
        if (!isLoggedIn) {
          e.preventDefault();
          onSignInClick();
        } else if (marketingConsent && !userGaveMarketingConsent) {
          e.preventDefault();
          if (isMarketingConsentEnabled()) {
            openMarketingConsent();
          }
        }
      } else if (selectedLinkURL) {
        trackButtonClick(selectedLinkURL);
      }
      // CTA banner
    } else {
      dispatch(openAuthOverlay("register"));
    }
  };

  const mediasLogoVariant: MediasVariant = accessLevelStr as MediasVariant;
  const mediasBlock: JSX.Element | undefined = <MediasLogo variant={mediasLogoVariant} />;
  let buttonIcon: JSX.Element | undefined;
  let buttonDisabled: boolean = gated && isLocked && marketingConsent && userGaveMarketingConsent ? true : false;
  if (isLocked && isLoggedIn && !marketingConsent) {
    buttonDisabled = true;
  }

  const isTeaserDropdown: boolean = teaserType === TeaserType.DROPDOWN && !!contentDropdown;
  const showCTAContainer: boolean = !isTeaserDropdown && (!!linkLabel || !!buttonLabel);
  // Whole teaser block can be clicked on
  const teaserIsClickable: boolean = !imageClickable && !linkLabel && !buttonLabel && !!hasLink && !(gated && isLocked);
  // Only the image can be clicked on
  const teaserImageIsClickable: boolean = !!hasLink && imageClickable && !(gated && isLocked);
  const mediaDataPipe: JSX.Element = <span className="scha__pipe scha__pipe--text">|</span>;

  let lockBlock: JSX.Element | undefined;
  let authenticationBlock: JSX.Element | undefined;
  let mediumBlock: JSX.Element | undefined;
  let availableLanguagesMenu: JSX.Element | undefined;
  let ctaBlock: JSX.Element | undefined;
  let imageBlock: JSX.Element | undefined;
  let toggleButton: JSX.Element | undefined;
  let contentBlock: JSX.Element | undefined;

  // lockBlock
  if (gated && isLocked) {
    buttonIcon = <LockIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />;

    lockBlock = (
      <div className="scha__teaser__lock">
        <span className="scha_teaser__lock__logo">{mediasBlock}</span>
        <span className="scha_teaser__lock__line scha__pipe scha__pipe--default">|</span>
        <span className="scha_teaser__lock__icon">
          <LockIcon sx={{ fontSize: 20 }} />
        </span>
      </div>
    );

    // When logged-in and marketing consent is necessary, we don't show the lock icon
    if (isLoggedIn && marketingConsent) {
      buttonIcon = undefined;
      lockBlock = undefined;
    }
  }

  // authenticationBlock
  if (gated && !isLoggedIn && teaserType !== TeaserType.STANDARD) {
    authenticationBlock = (
      <div className="scha__teaser__authentication copy_left f-carbon">
        {teaserType !== TeaserType.CTA && (
          <Link onClick={onRegisterClick} className={"f-pre"} component="button" type="button">
            {t("header.link.register")}
          </Link>
        )}
        {teaserType === TeaserType.CTA && (
          <TeaserButton className={"scha__teaser__button f-pre"} onClick={onRegisterClick} startIcon={<PersonIcon />}>
            {t("header.link.register")}
          </TeaserButton>
        )}
        <em className={"scha__teaser__authentication__or"}>{t("common.or")}</em>
        <Link onClick={onSignInClick} className={"f-pre"} component="button" type="button">
          {t("header.link.signIn")}
        </Link>
      </div>
    );
  }

  // mediaMetaData
  if (showMediaMetaData) {
    if (linkUrl && Array.isArray(linkUrl)) {
      const onLanguageMenuClick = (key: { language: string; url: string }) => {
        setSelectedUrl(key.url);
        setSelectedLanguage(key.language);
        handleClose();
      };

      if (linkUrl.length > 1) {
        availableLanguagesMenu = (
          <>
            <Link appearance={"navigation"} onClick={handleClick} component="button" type="button">
              <span>
                {selectedLanguage && selectedLanguage.length
                  ? t(`language.${selectedLanguage.toLowerCase()}`)
                  : t("Language")}
              </span>
              {isOpen ? (
                <KeyboardArrowUpIcon className={"icon-margin-l"} sx={{ fontSize: 20 }} />
              ) : (
                <KeyboardArrowDownIcon className={"icon-margin-l"} sx={{ fontSize: 20 }} />
              )}
            </Link>
            <Menu
              open={isOpen}
              anchorEl={anchorEl}
              onClose={handleClose}
              slotProps={{ list: { disablePadding: true } }}
            >
              {linkUrl.map((key, index) => (
                <MenuItem
                  key={index}
                  onClick={() => {
                    return onLanguageMenuClick(key);
                  }}
                >
                  {t(`language.${key.language.toLowerCase()}`)}
                </MenuItem>
              ))}
            </Menu>
          </>
        );
      } else if (linkUrl.length === 1) {
        const key = linkUrl[0];
        availableLanguagesMenu = <span>{t(`language.${key.language.toLowerCase()}`)}</span>;
      }
    }

    mediumBlock = (
      <div className="scha__teaser__media-meta-data">
        {mediaMetaData?.medium && <span className="scha__teaser__media-meta-data__entry">{mediaMetaData?.medium}</span>}
        {mediaMetaData?.medium && mediaMetaData?.mimeType && mediaDataPipe}
        {mediaMetaData?.mimeType && (
          <span className="scha__teaser__media-meta-data__entry">{mediaMetaData?.mimeType}</span>
        )}
        {mediaMetaData?.medium && mediaMetaData?.length && mediaDataPipe}
        {mediaMetaData?.length && (
          <span className="scha__teaser__media-meta-data__entry">
            {mediaMetaData?.length} {mediaMetaData?.lengthUnit}
          </span>
        )}
        {mediaMetaData?.medium && mediaMetaData?.size && mediaDataPipe}
        {mediaMetaData?.size && (
          <span className="scha__teaser__media-meta-data__entry">
            {mediaMetaData?.size} {mediaMetaData?.sizeUnit}
          </span>
        )}
        {mediaMetaData?.medium && availableLanguagesMenu && mediaDataPipe}
        {availableLanguagesMenu && (
          <span className="scha__teaser__media-meta-data__entry">{availableLanguagesMenu}</span>
        )}
      </div>
    );
  }

  // imageBlock
  if (medias && medias.length > 0) {
    const renderMediaElement = (media: SchaefflerMediaDTO, hasMultipleVideos?: boolean, showCaption?: boolean) => {
      let element = (
        <figure
          className={classNames({
            scha__teaser__image: teaserType !== TeaserType.CONTACT,
            "scha__teaser__image--clickable": teaserImageIsClickable || isLocked,
            "scha__teaser__image--with-videos": hasMultipleVideos,
          })}
          onClick={(e) => (teaserImageIsClickable || isLocked ? click(e) : null)}
        >
          <CMSMedia
            {...media}
            ratio={teaserType === TeaserType.CONTACT ? "1/1" : "16/9"}
            disabled={isLocked}
            className={classNames({
              "scha__forced-ratio-image-circular": teaserType === TeaserType.CONTACT,
              "scha__forced-ratio-image-with-border-radius": teaserType === TeaserType.PUBLICATION,
            })}
          />

          {showCaption && media.caption && (
            <figcaption className={"scha__teaser__image__caption"}>{media.caption}</figcaption>
          )}
        </figure>
      );

      // Untouched-with-reason (task 0049, row 46 area): HEAD renders this image-clickable
      // wrapper as a href-less anchor (teaserIsClickable and imageClickable are mutually
      // exclusive, so linkParams is always {} here — the img-level handler stopPropagation's
      // for tracking only, never navigation). Enabling navigation is a product decision — follow-up.
      if (teaserImageIsClickable) {
        const linkParams = teaserIsClickable
          ? {
              href: isUrlVisible ? selectedLinkURL : undefined,
              target: isUrlVisible ? (isExternal(target) ? "_blank" : undefined) : undefined,
            }
          : {};

        element = <a {...linkParams}>{element}</a>;
      }

      return element;
    };
    let mediaElement;
    if (medias.length === 1) {
      mediaElement = renderMediaElement(medias[0], false, teaserType === TeaserType.PUBLICATION);
    } else {
      const containsVideos = medias.some((m) => m.type === MediaType.video);
      mediaElement = (
        <Carousel
          data={medias}
          pageItemsCount={1}
          renderSlide={(data) => renderMediaElement(data.data[0], containsVideos)}
          controlsVariant={"inside"}
        />
      );
    }

    imageBlock = (
      <div className="scha__teaser__image-container">
        {mediaElement}
        {lockBlock}
      </div>
    );
  }

  // imageBlock
  if (teaserType === TeaserType.CTA) {
    imageBlock = (
      <div className="scha__teaser__image-container">
        <div className="scha__teaser__image scha__teaser__image--medias">{mediasBlock}</div>
      </div>
    );
  }

  // contentBlock
  if (teaserType !== TeaserType.CTA) {
    contentBlock = (
      <div className={classNames(`scha__teaser__content scha__teaser__content--${teaserType}`, mirrored && "ml-0")}>
        {headline && (
          <StyledHeadline title={headline} rootStyles={headlineStyles} teaserType={teaserType}>
            {headline}
          </StyledHeadline>
        )}
        {subHeadline && (
          <StyledSubheadline title={subHeadline} rootStyles={subHeadlineStyles} teaserType={teaserType}>
            {subHeadline}
          </StyledSubheadline>
        )}
        {content && (
          <TeaserText
            className={"scha__teaser__text copy_left f-carbon"}
            dangerouslySetInnerHTML={createMarkup(content)}
          />
        )}
      </div>
    );
    // CTA
    // CTA uses `headline` for the _content_ area.
  } else {
    contentBlock = (
      <div className={classNames("scha__teaser__content scha__teaser__content--cta", mirrored && "ml-0")}>
        {headline && <TeaserText className={"scha__teaser__text copy_left f-carbon"}>{headline}</TeaserText>}
      </div>
    );
  }

  // Call To Action Container
  if (showCTAContainer) {
    const linkParams = {
      href: isUrlVisible ? resolveUrl(selectedLinkURL ?? "") : undefined,
      target: isUrlVisible ? (isExternal(target) ? "_blank" : undefined) : undefined,
    };
    ctaBlock = (
      <div
        className={classNames("scha__teaser__cta-container", {
          "scha__teaser__cta-container--CONTACT": teaserType === TeaserType.CONTACT,
          "scha__teaser__cta-container--PUBLICATION": teaserType === TeaserType.PUBLICATION,
          "ml-0": mirrored,
        })}
      >
        {/* CTA as a link */}
        {linkLabel && hasLink && (
          <ArrowLink className={"scha__teaser__link"} onClick={click} {...linkParams}>
            {linkLabel}
          </ArrowLink>
        )}
        {/* Main CTA button */}
        {(buttonLabel || buttonIcon) && (
          <TeaserButton
            className={`scha__teaser__button scha__teaser__button--${teaserType}`}
            disabled={buttonDisabled}
            {...buttonProps}
            startIcon={buttonIcon}
            onClick={click}
            {...linkParams}
          >
            {buttonLabel}
          </TeaserButton>
        )}
        {/* Authentication and Registration */}
        {authenticationBlock}
      </div>
    );
  }

  if (teaserType === TeaserType.CTA) {
    ctaBlock = (
      <div className={"scha__teaser__cta-container scha__teaser__cta-container--cta"}>{authenticationBlock}</div>
    );
  }

  if (isTeaserDropdown && contentDropdown) {
    toggleButton = (
      <div className="scha__teaser__togglebutton--container">
        <div className="scha__teaser__toggletext">
          {isExpanded ? t("newsletter.consent.seeless") : t("newsletter.consent.seemore")}
        </div>
        <IconButton className={"scha__teaser__cta scha__teaser__togglebutton"} onClick={toggle}>
          <TeaserToggleIcon
            isActive={isExpanded}
            size={IconSize.FONT_16}
            data-test-id={"cms-teaser-btn-toggle"}
            className="scha__teaser__togglebutton--icon"
          />
        </IconButton>
      </div>
    );
  }

  const isPublicationDesktop = teaserType === TeaserType.PUBLICATION && isDesktop;

  // If "mirrored" prop is checked, the text and the image swap the sides (it doesn't apply
  // for tablet/mobile viewport)
  const output = (
    <>
      {isPublicationDesktop ? !mirrored && imageBlock : imageBlock}
      {showContentBox && (
        <>
          <div
            className={classNames("scha__teaser__container", {
              "scha__teaser__container--CONTACT": teaserType === TeaserType.CONTACT,
              "scha__teaser__container--PUBLICATION": teaserType === TeaserType.PUBLICATION,
            })}
          >
            {contentBlock}
            {mediumBlock}
            {ctaBlock}
          </div>
          {toggleButton}
        </>
      )}
      {isPublicationDesktop && mirrored && imageBlock}
    </>
  );

  const teaserClick = (e: React.MouseEvent<unknown>) => {
    if (toggles) {
      return toggle(e);
    } else if (teaserIsClickable) {
      return click(e);
    }
  };

  const teaserLinkParams = teaserIsClickable
    ? {
        href: isUrlVisible ? resolveUrl(selectedLinkURL ?? "") : undefined,
        target: isUrlVisible ? (isExternal(target) ? "_blank" : undefined) : undefined,
      }
    : {};

  const TeaserTag = teaserIsClickable ? "a" : "div";
  const teaserClasses = classNames(
    `scha__teaser scha__teaser--${teaserType} scha__teaser--${wideness}`,
    {
      "scha__teaser--hidden": !isVisible,
      "scha__teaser--active": isExpanded,
      "scha__teaser--clickable": teaserIsClickable,
      "scha__teaser--toggles": toggles,
      "scha__teaser--gated": gated,
      "scha__teaser--gated-locked": gated && isLocked,
      "scha__teaser--gated-unlocked": gated && !isLocked,
      "scha__teaser--gated-marketingconsent": gated && marketingConsent,
      "scha__teaser--loggedin": isLoggedIn,
      "scha__teaser--not-loggedin": !isLoggedIn,
    },
    className,
  );
  // Site-relative internal links navigate through the router; external / new-tab /
  // non-clickable teasers keep the exact HEAD rendering.
  const typedTeaserUrl =
    teaserIsClickable && isUrlVisible && selectedLinkURL && isSiteRelativeUrl(selectedLinkURL) && !isExternal(target)
      ? selectedLinkURL
      : undefined;

  const card = (
    <>
      {typedTeaserUrl ? (
        <RouterLink className={teaserClasses} id={`CMSTeaser-${index}`} onClick={teaserClick} to={typedTeaserUrl}>
          {output}
        </RouterLink>
      ) : (
        <TeaserTag className={teaserClasses} id={`CMSTeaser-${index}`} onClick={teaserClick} {...teaserLinkParams}>
          {output}
        </TeaserTag>
      )}
      {marketingConsentPopupOpen && (
        <ConsentPopup
          close={closeMarketingConsent}
          consentIntro={consentIntro}
          popupTitle={popupTitle}
          onOk={onAcceptMarketingConsent}
        />
      )}

      {isLoginTimeoutErrorVisible() && <ToastMessage type={"info"}>{t("login.error.timeout")}</ToastMessage>}
    </>
  );

  if (!buttonLabel && !buttonIcon && teaserType === TeaserType.STANDARD && teaserIsClickable) {
    return (
      <Card
        sx={{
          display: "flex",
          borderWidth: "0px",
          height: "100%",
        }}
      >
        <CardActionArea
          sx={{
            display: "flex",
            alignItems: "stretch",
            borderRadius: "12px",
          }}
        >
          {card}
        </CardActionArea>
      </Card>
    );
  }

  return card;
};

const StyledCMSTeaser = styled(CMSTeaser, { shouldForwardProp: (prop) => prop !== "rootStyles" })(
  // Migrated from CMSTeaser.less: .scha__teaser base + shared internals +
  // STANDARD family + gated modifiers. Variant families land on their own
  // keys; ancestors/descendants match the rendered classes exactly.
  ({ theme }) => ({
    // Migrated from CMS.less (.scha__teaser frame): card frame; the later
    // `.scha__cms, .scha__teaser { display: block }` typography rule from the
    // same sheet is outranked here, matching the original cascade where the
    // frame block came after it.
    display: "flex",
    flexFlow: "column",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    backgroundColor: "#ffffff",
    borderRadius: "12px",

    overflow: "hidden",
    position: "relative",
    height: "100%",

    "&.scha__teaser--active": {
      borderColor: C_PRIMARY,
    },

    // @screen-phone-max = @screen-tablet - 1px = 767px, preserved raw
    // (theme down("tablet") would emit 767.95px).
    "@media (max-width: 767px)": {
      width: "100%",
    },

    "&.scha__teaser--CONTACT, &.scha__teaser--PUBLICATION": {
      borderRadius: 0,
      borderWidth: 0,
      overflow: "visible",
    },

    "&.scha__teaser--clickable, &.scha__teaser--toggles": {
      cursor: "pointer",
    },

    "&.scha__teaser--clickable": {
      color: "inherit",
    },

    "& .scha__teaser__image": {
      aspectRatio: "16 / 9",
      "& img": {
        width: "100%",
        height: "100%",
      },
      "&.scha__teaser__image--clickable": {
        cursor: "pointer",
      },
      "&.scha__teaser__image--with-videos": {
        paddingBottom: "32px",
        display: "flex",
        justifyContent: "center",
        background: "black",
      },
    },

    "& .scha__teaser__media-meta-data": {
      display: "block",
      marginTop: "8px",
      marginRight: "16px",
      marginBottom: "16px",
      marginLeft: "16px",
      "&:not(:last-child)": {
        marginBottom: 0,
      },
      "& span": {
        display: "inline-block",
        "&.scha__pipe": {
          width: "20px",
          "&.scha__pipe--text": {
            overflow: "hidden",
            height: "1rem",
          },
        },
      },
    },

    // from CMS.less (.scha__pipe variants; rendered only by CMSTeaser)
    "& .scha__pipe--default": {
      color: "transparent",
      borderLeft: `1px solid ${C_OUTLINE_VARIANT}`,
    },

    "& .scha__pipe--text": {
      color: "inherit",
      textAlign: "center",
    },

    "& .scha__teaser__content": {
      display: "flex",
      flexDirection: "column",
      marginBottom: "12px",
      marginRight: "16px",
      marginLeft: "16px",
      "&:last-child": {
        marginBottom: 0,
      },
      "& .headline_h5_center, & .headline_h6_center": {
        fontWeight: "bold",
      },
      // Variant blocks migrate together with the base: the styled-root
      // descendant specificity (0,2,0) would otherwise outrank the LESS
      // variant overrides (0,1,0) still living in main.css.
      "&.scha__teaser__content--CONTACT": {
        alignItems: "center",
        textAlign: "center",
        marginRight: 0,
        marginLeft: 0,
        "& .headline_h5_left, & .headline_h6_left": {
          ...getFontStyle(FontSize.HeadlineSmall),
          fontWeight: "bold",
        },
        "& .copy_left": {
          ...getFontStyle(FontSize.BodyLarge),
        },
      },
      "&.scha__teaser__content--PUBLICATION": {
        marginTop: 0,
        paddingBottom: 0,
        // LESS @screen-tablet max-width is exactly 768px (theme down()
        // would emit 767.95px) — preserved raw.
        "@media (max-width: 768px)": {
          marginRight: 0,
          marginLeft: 0,
        },
        "& .headline_h5_left": {
          ...getFontStyle(FontSize.TitleLarge),
          fontWeight: "bold",
        },
        "& .headline_h6_left--label": {
          ...getFontStyle(FontSize.BodyMedium),
          fontWeight: "bold",
        },
        "& .copy_left": {
          ...getFontStyle(FontSize.BodyMedium),
          fontWeight: 400,
        },
      },
    },

    "& .medias-logo-text": {
      height: "22px",
      "&.medias-logo-text--plus-business": {
        height: "42px",
      },
    },

    "& .scha__teaser__container": {
      paddingTop: "16px",
      paddingBottom: "16px",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      "&.scha__teaser__container--CONTACT": {
        paddingTop: "16px",
      },
      "&.scha__teaser__container--PUBLICATION": {
        paddingTop: "16px",
        paddingBottom: 0,
        [theme.breakpoints.up("tablet")]: {
          paddingTop: 0,
        },
      },
    },

    "&.scha__teaser--STANDARD": {
      "& .scha__teaser__container": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flex: 1,
      },
    },

    "&.scha__teaser--DROPDOWN": {
      "& .scha__teaser__togglebutton--container": {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginRight: "16px",
        marginLeft: "16px",
        color: C_PRIMARY,
        marginBottom: "16px",
        "& .scha__teaser__toggletext": {
          ...getFontStyle(FontSize.LabelLarge),
        },
      },
      "& .scha__teaser__togglebutton--icon": {
        color: C_PRIMARY,
      },
    },

    "&.scha__teaser--gated-locked": {
      "& .scha__teaser__image-container": {
        position: "relative",
      },
      // @c-background-color-white = @c-white
      "& .scha__teaser__image": {
        backgroundColor: "#ffffff",
        opacity: 0.2,
      },
    },

    // overrules --gated-locked (kept after it in source order)
    "&.scha__teaser--CTA, &.scha__teaser--gated-marketingconsent.scha__teaser--loggedin": {
      "& .scha__teaser__image": {
        backgroundColor: "transparent",
        opacity: 1,
      },
    },

    "& .scha__teaser__cta-container": {
      display: "flex",
      flexDirection: "column",
      marginTop: "auto",
      marginRight: "16px",
      marginLeft: "16px",
      "& .scha__teaser__cta": {
        margin: "0 auto",
        textAlign: "center",
        [theme.breakpoints.up("tablet")]: {
          margin: "0 8px",
          textAlign: "left",
        },
      },
      "&.scha__teaser__cta-container--CONTACT": {
        paddingTop: "8px",
        marginRight: 0,
        marginLeft: 0,
      },
      "&.scha__teaser__cta-container--PUBLICATION": {
        // LESS @screen-tablet max-width is exactly 768px — preserved raw.
        "@media (max-width: 768px)": {
          marginRight: 0,
          marginLeft: 0,
        },
      },
    },

    // Migrated from CMSTeaser.less (.scha__teaser--PUBLICATION layout).
    "&.scha__teaser--PUBLICATION": {
      border: "none",
      borderRadius: 0,

      "& .scha__teaser__cta": {
        justifyContent: "center",
      },

      [theme.breakpoints.up("tablet")]: {
        width: "auto",
        display: "flex",
        flexDirection: "row",
        flex: 1,

        "& .scha__teaser__content, & .scha__teaser__media-meta-data": {
          marginLeft: "32px",
        },

        "& .scha__teaser__cta-container": {
          marginLeft: "32px",
          flexDirection: "row",
          "& .scha__teaser__cta": {
            marginTop: 0,
          },
        },

        "& .scha__teaser__image-container": {
          flex: "0 0 328px",
          overflow: "hidden",
        },

        "& .scha__teaser__image__caption": {
          ...getFontStyle(FontSize.LabelMedium),
          [theme.breakpoints.up("tablet")]: {
            ...getFontStyle(FontSize.LabelLarge),
          },
        },

        "& .scha__teaser__container": {
          flex: 2,
        },
      },

      [theme.breakpoints.up("desktop-s")]: {
        minWidth: "928px",
        "& .scha__teaser__image-container": {
          flex: "0 0 448px",
        },
      },
    },

    // Migrated from CMSTeaser.less (.scha__teaser--CTA layout).
    "&.scha__teaser--CTA": {
      width: "auto",
      maxWidth: "448px",
      margin: "16px auto",

      "& .scha__teaser__image": {
        backgroundColor: "transparent",
      },

      "& .scha__teaser__image-container": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },

      // The LESS `--CTA .scha__teaser--gated-locked` "should not happen"
      // block was a descendant selector that can never match (both classes
      // render on the same teaser element) — dropped with proof.

      // @screen-desktop-s-max = 1536px - 1 = 1535px, preserved raw.
      "@media (max-width: 1535px)": {
        "& .scha__teaser__content": {
          marginLeft: 0,
          marginRight: 0,
        },

        "& .scha__teaser__image-container": {
          borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
          minHeight: "50px",
          margin: "16px",
          marginTop: 0,
          padding: "16px 0",
        },

        "& .scha__teaser__container": {
          margin: "16px",
        },

        "& .scha__teaser__cta-container": {
          marginTop: 0,
        },
      },

      [theme.breakpoints.up("desktop-s")]: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "928px",
        maxWidth: "initial",
        minHeight: "82px",

        "& .scha__teaser__image-container": {
          flex: "0 0 145px",
          margin: "0 16px",
          paddingRight: "16px",
          borderRight: `1px solid ${C_OUTLINE_VARIANT}`,
          minHeight: "50px",
        },

        "& .scha__teaser__container": {
          flex: 2,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          marginTop: 0,
        },

        "& .scha__teaser__content--cta": {
          flex: 1,
          marginTop: "8px",
          marginRight: 0,
        },

        "& .scha__teaser__cta-container": {
          flex: "0 0 375px",
          marginTop: "16px",
        },
      },
    },

    // Last on purpose: in CMSTeaser.less this rule sat at the end of the
    // file, after the variant blocks — it must keep winning the cascade
    // against --CTA's desktop-s display:flex (CTA teasers stay hidden).
    "&.scha__teaser--hidden": {
      display: "none",
    },

    "& .scha__teaser__headline": {
      marginTop: 0,
      marginBottom: 0,
    },

    "& .scha__teaser__text": {
      marginTop: "8px",
      "& ul": {
        listStyleType: "disc",
      },
    },

    "& .scha__teaser__lock": {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      padding: "16px",
      display: "flex",
      width: "185px",
      alignItems: "center",
      pointerEvents: "none",
      // from CMS.less (.scha__teaser__lock frame)
      border: `1px solid ${C_OUTLINE_VARIANT}`,
      backgroundColor: "#ffffff",
      "& span": {
        flex: 1,
        // NOTE: single-underscore class is rendered as-is by CMSTeaser
        // (scha_teaser__lock__icon).
        "&.scha_teaser__lock__icon": {
          display: "flex",
          flex: "0 0 20px",
          color: C_PRIMARY,
        },
        "&.scha__pipe": {
          flex: "0 0 10px",
          margin: "0 8px 0 16px",
        },
      },
    },

    "& .scha__teaser__authentication": {
      marginTop: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flexWrap: "wrap",
      [theme.breakpoints.up("tablet")]: {
        marginTop: 0,
        flexDirection: "row",
        marginLeft: "24px",
      },
    },

    "& .scha__teaser__authentication__or": {
      display: "block",
      margin: "4px 0",
      [theme.breakpoints.up("tablet")]: {
        margin: "0 16px",
      },
    },

    "& .scha__teaser__image-container": {
      alignSelf: "flex-start",
      width: "100%",
    },
  }),
  ({ rootStyles }: { rootStyles?: string }) => rootStyles,
);

export default StyledCMSTeaser;

function isExternal(target: string | undefined) {
  target = target?.toUpperCase() ?? "";
  return target === BLANK || target === _BLANK;
}

interface HeadlineProps {
  title: string;
  rootStyles?: string;
  teaserType: TeaserType;
}

const BaseHeadline = styled("h3", {
  shouldForwardProp: (prop) => prop !== "teaserType",
})<HeadlineProps>(({ teaserType }) => ({
  ...getFontStyle(teaserType === TeaserType.CONTACT ? FontSize.HeadlineSmall : FontSize.TitleMedium),
  fontWeight: "bold",
}));

const BaseSubheadline = styled("h3", {
  shouldForwardProp: (prop) => prop !== "teaserType",
})<HeadlineProps>(({ teaserType }) => ({
  ...getFontStyle(teaserType === TeaserType.CONTACT ? FontSize.TitleMedium : FontSize.LabelLarge),
  fontWeight: "bold",
  color: C_ON_SURFACE_VARIANT,
}));

const StyledHeadline = makeExternallyStyledElement(BaseHeadline);
const StyledSubheadline = makeExternallyStyledElement(BaseSubheadline);

const TeaserText = styled("div")({
  overflowWrap: "anywhere",
});

// Migrated from CMSTeaser.less: the global `.button` min-width overrule
// (110px mobile / 140px from desktop-s) that used to apply to every `.button`
// user, plus the `.scha__teaser__cta-container .scha__teaser__button--*`
// variant widths (CONTACT/STANDARD/PUBLICATION are 140px at every viewport;
// CONTACT also centers via margin auto). The variant keys are keyed on the
// classes this component renders — equivalent to the ancestor-scoped LESS
// rules because this is the only renderer of those classes.
const TeaserButton = styled(Button)(({ theme }) => ({
  minWidth: "110px",
  [theme.breakpoints.up("desktop-s")]: {
    minWidth: "140px",
  },
  "&.scha__teaser__button--CONTACT, &.scha__teaser__button--STANDARD, &.scha__teaser__button--PUBLICATION": {
    minWidth: "140px",
  },
  "&.scha__teaser__button--CONTACT": {
    margin: "auto",
  },
}));
